import React, { useState, useEffect } from "react";
import { useParams, useLocation, Navigate } from "react-router-dom";
import {
  ProductInfo,
  ProductImages,
  FeaturesList,
  TechnicalDetails,
  FAQSection,
} from "../components/camperDetails";
import EnhancedBookingForm from "../components/camperDetails/EnhancedBookingForm";
import { Header, WhySunCatchers } from "../components/shared";
import LoadingSpinner from "../components/shared/LoadingSpinner";
import { CheckoutFooter } from "../components/checkout";
import { fetchVehicleDetails } from "../services/vehicleService";
import { useTranslation } from "../hooks/useTranslation";
import { MEDIA_URL } from '../config/constants';
import { SEO, generateProductSchema, generateFAQSchema, generateBreadcrumbSchema } from "../seo";
import "../styles/camperDetails/CamperDetailsBase.css";
import "../styles/camperDetails/FamilyCatchersTheme.css";

const FamilyCatchersDetails = ({ camperData }) => {
  const { id } = useParams();
  const location = useLocation();
  const { t } = useTranslation('camperDetails');
  const [vehicleData, setVehicleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Get vehicle ID from navigation state first, then route param, then default
  // This allows clicking different Family Catchers to show the correct vehicle
  const vehicleId = location.state?.vehicleId || (id === 'family-catchers' ? 4 : parseInt(id) || 4);

  useEffect(() => {
    let isCancelled = false;

    const loadVehicleData = async () => {
      try {
        setLoading(true);
        const data = await fetchVehicleDetails(vehicleId);

        // Only update state if the component is still mounted and request wasn't cancelled
        if (!isCancelled) {
          setVehicleData(data);
        }
      } catch (err) {
        if (!isCancelled) {
          setError(err.message);
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    loadVehicleData();

    // Cleanup function to cancel the request if component unmounts
    return () => {
      isCancelled = true;
    };
  }, [vehicleId]);

  // For dynamic vehicle IDs, check vehicle_design from loaded data
  if (vehicleData && vehicleData.VehicleDesign?.String === 'Sun Catcher') {
    return <Navigate to="/campervan/sun-catchers" replace />;
  }
  if (vehicleData && vehicleData.VehicleDesign?.String === 'Wave Catcher') {
    return <Navigate to="/campervan/wave-catchers" replace />;
  }

  if (loading) {
    return <LoadingSpinner text="Loading vehicle details..." />;
  }

  if (error || !vehicleData) {
    return <div>Error loading vehicle details: {error}</div>;
  }

  // Static images for Family Catcher - simpler than database
  const staticImages = [
    `${MEDIA_URL}/images/FAMILY CATCHER/FamilyCatcher0001.webp`,
    `${MEDIA_URL}/images/FAMILY CATCHER/FamilyCatcher0002.webp`,
    `${MEDIA_URL}/images/FAMILY CATCHER/FamilyCatcher0003.webp`,
    `${MEDIA_URL}/images/FAMILY CATCHER/FamilyCatcher0004.webp`,
    `${MEDIA_URL}/images/FAMILY CATCHER/FamilyCatcher0005.webp`,
    `${MEDIA_URL}/images/FAMILY CATCHER/FamilyCatcher0006.webp`,
    `${MEDIA_URL}/images/FAMILY CATCHER/FamilyCatcher0007.webp`,
    `${MEDIA_URL}/images/FAMILY CATCHER/FamilyCatcher0008.webp`,
    `${MEDIA_URL}/images/FAMILY CATCHER/FamilyCatcher0009.webp`,
  ];

  const mainImage = staticImages[0];
  const thumbnailImages = staticImages.slice(1, 4); // Show 3 thumbnails
  const allImages = staticImages; // All images for gallery modal

  // Transform API data to component format with family-oriented features
  const camperDataFormatted = {
    id: vehicleData.ID.toString(),
    name: vehicleData.Name?.String || "Family Catchers",
    description: vehicleData.Description?.String || "Meet the Family Catcher, our spacious and comfortable campervan designed specifically for families. With extra storage, child-friendly features, and room for everyone, it's perfect for creating unforgettable memories on your Portuguese adventure.",
    price: `$${vehicleData.DailyRate?.String || "95.00"}`,
    priceDescription: "Per Night",
    mainImage: mainImage,
    thumbnailImages: thumbnailImages,
    allImages: allImages, // All images for modal
    additionalImagesCount: 0,
    features: vehicleData.Features || {
      bathroom: [],
      bedroom: [],
      outdoor: [],
      kitchen: [],
      themeSpecs: [],
    },
    technicalDetails: [
      {
        icon: "model",
        title: t("technicalDetails.model"),
        description: "Fiat Ducato",
      },
      {
        icon: "engine",
        title: t("technicalDetails.power"),
        description: "140HP, 2.2l Engine",
      },
      {
        icon: "dimensions",
        title: t("technicalDetails.lengthHeight"),
        description: "599cm x 252cm",
      },
      {
        icon: "assistance",
        title: t("technicalDetails.driverAssistance"),
        description: "Front and rear parking sensors",
      },
    ],
    faqs: t('familyCatcherFaqs', { returnObjects: true }).map((faq, index) => ({
      ...faq,
      isOpen: index === 0,
    })),
  };

  const camper = camperDataFormatted;

  // SEO Schemas
  const productSchema = generateProductSchema({
    name: camper.name,
    description: camper.description,
    image: camper.mainImage,
    dailyRate: vehicleData.DailyRate?.String || '60',
    slug: 'campervan/family-catchers',
    rating: 4.9,
    reviewCount: 8,
  });

  const faqSchema = generateFAQSchema(camper.faqs);

  const breadcrumbSchema = generateBreadcrumbSchema([
    { name: 'Home', path: '' },
    { name: 'Catalog', path: '/catalog' },
    { name: camper.name, path: '/campervan/family-catchers' },
  ]);

  return (
    <div className="family-catchers-page">
      <SEO
        title={`${camper.name} | Family Campervan for 5 | SunCatchers`}
        description={camper.description}
        image={camper.mainImage}
        schema={[productSchema, faqSchema, breadcrumbSchema].filter(Boolean)}
      />
      <h1 className="sr-only">{camper.name} - Family Campervan Rental Portugal</h1>
      <div className="main-container">
        <Header />
        <div className="content-container">
          <div className="left-column">
            <div className="left-column-content">
              <ProductInfo
                name={camper.name}
                description={camper.description}
              />
              <ProductImages
                mainImage={camper.mainImage}
                thumbnailImages={camper.thumbnailImages}
                allImages={camper.allImages}
                additionalCount={camper.additionalImagesCount}
                vehicleName="Swell Catcher"
              />
            </div>
            <WhySunCatchers />
            <FeaturesList features={camper.features} />
            <TechnicalDetails details={camper.technicalDetails} />
            <FAQSection faqs={camper.faqs} />
          </div>
          <div className="booking-container">
            <EnhancedBookingForm
              price={camper.price}
              priceDescription={camper.priceDescription}
              vehicleId={vehicleId}
              vanTitle={camper.name}
              vanImage={camper.mainImage}
            />
          </div>
        </div>
      </div>
      <CheckoutFooter />
    </div>
  );
};

export default FamilyCatchersDetails;